import { Component, OnDestroy } from '@angular/core';
import { mergeMap, map, filter, take, takeUntil, catchError } from 'rxjs/operators';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { APP_TITLE } from './app-routing.module';
import { TranslateService, TranslationChangeEvent } from '@ngx-translate/core';
import { AbLogin } from './abstract/content-pages/login/login.abstract';
import { AbLocale } from './abstract/full-pages/locale/locale.abstract';
import { AuthService, IID } from './shared/auth/auth.service';
import { hasPerm, Permission } from './shared/auth/permission.enum';
import { EMPTY, Subject } from 'rxjs';
import { AbFeatureFlag } from './abstract/services/feature-flag.abstract';
import { environment } from 'environments/environment';
import { DssTemaEnum } from 'enums/dss-tema.enum';
import { TenantService } from 'app/pages/full-pages/admin/tenant/tenant.service';
import { UsuarioService } from 'app/pages/full-pages/admin/usuario.service';
import { DesignSystemSigmaService } from 'app/services/design-system-sigma.service';
import { AbConfigPsim } from 'app/abstract/full-pages/config-psim/config-psim.abstract';
import { ConfigPSIM } from './models/configpsim.model';
import { AbPostHog } from './abstract/services/posthog.abstract';
import { PostHogEventEnum } from 'enums/posthog-event.enum';
import { capturarPageview } from 'app/services/posthog/posthog-pageview';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnDestroy {
  private skin: any = [];
  private liberado: boolean = false;
  private readonly destroy$ = new Subject();
  private readonly urlOriginSigmaClient = environment.urlOriginSigmaClient;
  private themeCurrent: DssTemaEnum = DssTemaEnum.DARK;
  private tokenSetted: boolean = false;
  private idiomaPronto: boolean = false;
  private localesResolvidos: boolean = false;
  private readonly LIMITE_ESPERA_IDIOMA_MS = 8000;
  private readonly INTERVALO_ESPERA_TOKEN_MS = 500;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private authService: AuthService,
    private loginService: AbLogin,
    private localeService: AbLocale,
    public translate: TranslateService,
    private tenantService: TenantService,
    private userService: UsuarioService,
    private dssService: DesignSystemSigmaService,
    private configPSIMService: AbConfigPsim,
    private posthog: AbPostHog,
    private featureFlagService: AbFeatureFlag
  ) {
    if (this.isDssLayout()) {
      window.addEventListener(
        'message',
        (event) => {
          if (event.origin !== this.urlOriginSigmaClient) return;

          if (typeof event.data === 'object') {
            this.themeCurrent = this.setTheme(event.data.theme);
            this.saveAuthentication(
              event.data.token,
              event.data.integradorId,
              event.data.tenant
            );
            this.dssService.applyTheme(this.themeCurrent);
          }
        },
        false
      );
      window.parent.postMessage(
        'center está apto a receber mensagens do sigma',
        this.urlOriginSigmaClient
      );
    } else {
      this.tokenSetted = true;
    }
  }

  private saveAuthentication(token, integradorId, tenant): void {
    sessionStorage.setItem(IID, integradorId);
    this.authService.saveToken(token);

    this.featureFlagService.carregarCapabilitiesPrivadas()
      .pipe(takeUntil(this.destroy$.asObservable()))
      .subscribe({
        complete: () => {
          this.tokenSetted = true;
        }
      });

    this.tenantService.limparTenantStorage();
    this.tenantService.salvarTenantSchemaStorage(tenant);

    if (this.podeLerConta()) {
      this.tenantService
        .getTenantConta()
        .pipe(
          catchError(() => EMPTY),
          takeUntil(this.destroy$.asObservable())
        ).subscribe({
        next: (conta: any) => {
          this.tenantService.limparTenantSchemaStorage();
          this.tenantService.salvarTenantStorage(conta);
          this.tenantService.salvarNomeTenantStorage(conta.nomeConta);
          this.featureFlagService.carregarCapabilitiesConta()
            .pipe(takeUntil(this.destroy$.asObservable()))
            .subscribe();
        },
      });}

    this.userService
      .getUsuario()
      .pipe(takeUntil(this.destroy$.asObservable()))
      .subscribe({
        next: (user: any) => {
          this.authService.saveUsuarioStorage(user);
        },
        complete: () => {
          window.parent.postMessage('loaded', this.urlOriginSigmaClient);
        },
      });

    if (this.podeLerConfigPsim()) {
      this.configPSIMService
        .getConfig()
        .pipe(
          catchError(() => EMPTY),
          takeUntil(this.destroy$)
        )
        .subscribe((configPSIM: ConfigPSIM) => {
          const sistemaPSIM = JSON.stringify(configPSIM.sistemaPSIM);
          const psimHabilitado = JSON.stringify(configPSIM.habilitar);
          sessionStorage.setItem('sistemaPSIM', sistemaPSIM);
          sessionStorage.setItem('psimHabilitado', psimHabilitado);
          sessionStorage.setItem(
            'webhookHabilitado',
            JSON.stringify(configPSIM.webhookHabilitado)
          );
        });
    }
  }

  private podeLerConta(): boolean {
    return hasPerm(Permission.CONTA_READ);
  }

  private podeLerConfigPsim(): boolean {
    return hasPerm(Permission.CONTA_READ) || hasPerm(Permission.PESSOA_READ);
  }

  private setTheme(theme: string): DssTemaEnum {
    sessionStorage.setItem('theme', theme);
    return theme as DssTemaEnum;
  }

  private tratarTitulo(): void {

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) route = route.firstChild;
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data)
      )
      .pipe(takeUntil(this.destroy$.asObservable()))
      .subscribe((event) => {
        const title = this.translate.instant(event['title'] ? event['title'] : APP_TITLE);
        this.titleService.setTitle(title);
      });

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$.asObservable())
      )
      .subscribe((event) => {
        const url = event.urlAfterRedirects || event.url;
        capturarPageview(this.posthog, url, this.isDssLayout() ? 'dss' : 'padrao');
      });
  }

  private tratarSkinFavicon(): void {
    if (this.authService.isAuthenticated) {
      this.loginService
        .getCustomSkin()
        .pipe(takeUntil(this.destroy$.asObservable()))
        .subscribe((skin) => {
          if (skin && skin.favicon) {
            const url = skin.favicon.startsWith('http');
            this.skin.favicon =
              (url ? '' : 'data:image/x-icon;base64,') + skin.favicon;
            document
              .getElementById('favicon')
              .setAttribute('href', this.skin.favicon);
          }
        });
    }
  }

  private tratarDefaultLang(): void {
    this.translate.addLangs(['pt_BR']);
    this.aguardarDicionarioIdioma();
    this.translate.setDefaultLang('pt_BR');
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$.asObservable()))
      .subscribe((params: TranslationChangeEvent) => {
        this.authService.saveLocale(params.lang);

        const url = this.router.url;
        const queryParams = this.router.parseUrl(this.router.url).queryParams;
        if (
          !url.includes('login') &&
          !url.includes('pages/pv?v') &&
          !url.includes('pages/convidado') &&
          url !== '/'
        ) {
          this.router
            .navigateByUrl('/', { skipLocationChange: true })
            .then(() => {
              this.router.navigate([url.split('?')[0]], { queryParams });
            });
        }
      });
    const browserLang = this.authService.getLocale();

    this.localeService
      .getLocales()
      .pipe(takeUntil(this.destroy$.asObservable()))
      .subscribe({
        next: (locales: any) => {
          if (locales) {
            this.translate.addLangs(
              locales.map((l) => `${l.language}_${l.country}`)
            );

            if (this.authService.isAuthenticated())
              this.translate.use(
                browserLang && this.translate.langs.includes(browserLang)
                  ? browserLang
                  : this.translate.defaultLang
              );
            this.localeService
              .getLocaleUsuarioOrIntegrador()
              .pipe(takeUntil(this.destroy$.asObservable()))
              .subscribe({
                next: (locale) => {
                  if (locale)
                    this.translate.use(`${locale.language}_${locale.country}`);
                  else this.translate.use(this.translate.defaultLang);
                },
                error: () => {
                  this.translate.use(this.translate.defaultLang);
                },
              });
          }
        },
        error: (erro) => {
          console.error('[AppComponent] falha ao carregar os idiomas suportados', erro);
          this.posthog?.capture(PostHogEventEnum.API_ERRO, {
            contexto: 'bootstrap_locale',
            endpoint: 'public/locale/all/',
            erro_mensagem: erro?.message ?? String(erro),
          });
          this.localesResolvidos = true;
          this.avaliarLiberacao();
        },
        complete: () => {
          this.localesResolvidos = true;
          this.avaliarLiberacao();
        },
      });
  }

  private aguardarDicionarioIdioma(): void {
    this.translate.onDefaultLangChange
      .pipe(take(1), takeUntil(this.destroy$.asObservable()))
      .subscribe(() => {
        this.idiomaPronto = true;
        this.avaliarLiberacao();
      });

    setTimeout(() => {
      if (!this.idiomaPronto) {
        console.error('[AppComponent] dicionario de idioma indisponivel dentro do limite de espera');
        this.idiomaPronto = true;
        this.avaliarLiberacao();
      }
    }, this.LIMITE_ESPERA_IDIOMA_MS);
  }

  private avaliarLiberacao(): void {
    if (this.idiomaPronto && this.localesResolvidos) {
      this.liberado = true;
    }
  }

  public ngOnInit(): void {
    this.tratarTitulo();
    this.tratarSkinFavicon();
    if (this.tokenSetted) {
      this.tratarDefaultLang();
      return;
    }
    const interval = setInterval(() => {
      if (this.tokenSetted) {
        this.tratarDefaultLang();
        clearInterval(interval);
      }
    }, this.INTERVALO_ESPERA_TOKEN_MS);
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public isLiberado(): boolean {
    return this.liberado;
  }

  isDssLayout(): boolean {
    return this.dssService.isDssLayout();
  }
}
